import React, { useState, useEffect, Fragment } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import {
  selectSubscriptionBundle,
  selectSubscriptionUpsells,
  selectSubscriptionStep,
  selectSubscriptionUpgrades
} from '../../store/subscription/subscription.selector'
import {
  DIAPERS_BUNDLE,
  MIX_BUNDLE,
  PANTS_BUNDLE,
  UPSELLS_DATA,
  UPSELL_STEP
} from '../../utils/bab/bab.utils'
import { getCurrencyFormat } from '../../utils/utils'

import './subscription-overview.styles.scss'

const SubscriptionOverview = () => {
  const { t } = useTranslation('updatedBAB')

  const subscriptionBundle = useSelector(selectSubscriptionBundle)
  const subscriptionUpgrades = useSelector(selectSubscriptionUpgrades)
  const subscriptionUpsells = useSelector(selectSubscriptionUpsells)
  const subscriptionStep = useSelector(selectSubscriptionStep)

  const isUpsellStep = UPSELL_STEP.index === subscriptionStep

  const basePrice =
    subscriptionBundle.id === 'mix'
      ? MIX_BUNDLE.basePrice
      : subscriptionBundle.id === 'pants'
        ? PANTS_BUNDLE.basePrice
        : DIAPERS_BUNDLE.basePrice

  const [subTotalPrice, setSubTotalPrice] = useState(basePrice)
  const [subscriptionPrice, setSubscriptionPrice] = useState(basePrice)
  const [upgradesPrice, setUpgradesPrice] = useState(0)
  const [upsellsPrice, setUpsellsPrice] = useState(0)

  const formattedSubscriptionPrice = getCurrencyFormat(subscriptionPrice)
  const formattedUpsellsPrice = getCurrencyFormat(upsellsPrice)
  const formattedSubTotalPrice = getCurrencyFormat(subTotalPrice)

  useEffect(() => {
    const newUpgradesPrice =
      subscriptionUpgrades.length > 0
        ? subscriptionUpgrades.reduce((acc, upgradeSku) => {
          const upgradeData = UPSELLS_DATA.find((upsell) =>
            upsell.variants.find(
              (variant) => variant.upgradeData.sku === upgradeSku
            )
          )

          if (!upgradeData) {
            console.error(`Error processing upgrade "${upgradeSku} :`)
            return acc
          }

          const upgradePrice = upgradeData.variants[0].upgradeData.price

          return (acc = acc + upgradePrice)
        }, 0)
        : 0

    const newUpsellsPrice =
      subscriptionUpsells.length > 0
        ? subscriptionUpsells.reduce((acc, upsellSku) => {
          const upsellData = UPSELLS_DATA.find((upsell) =>
            upsell.variants.find(
              (variant) => variant.upsellData.sku === upsellSku
            )
          )

          if (!upsellData) {
            console.error(`Error processing upsell "${upsellSku} :`)
            return acc
          }

          const upsellPrice = upsellData.variants[0].upsellData.price

          return (acc = acc + upsellPrice)
        }, 0)
        : 0

    setUpgradesPrice(newUpgradesPrice)
    setUpsellsPrice(newUpsellsPrice)
    setSubscriptionPrice(basePrice + newUpgradesPrice)
    setSubTotalPrice(basePrice + newUpgradesPrice + newUpsellsPrice)
  }, [subscriptionUpgrades, subscriptionUpsells])

  return (
    <div
      className='subscription-overview'
      data-multi={
        isUpsellStep && (upgradesPrice !== 0 || upsellsPrice !== 0) ? '' : null
      }
    >
      {isUpsellStep && (upgradesPrice !== 0 || upsellsPrice !== 0) ? (
        <Fragment>
          <div className='subscription-line'>
            <p>{t('navigation.subscriptionOverview.upgrades')}</p>
            <p>{formattedSubscriptionPrice}</p>
          </div>
          {upsellsPrice > 0 && (
            <div className='subscription-line'>
              <p>{t('navigation.subscriptionOverview.upsells')}</p>
              <p>{formattedUpsellsPrice}</p>
            </div>
          )}
          <div className='subscription-info'>
            <p>{t('navigation.subscriptionOverview.subTotal')}</p>
            <p>{`${formattedSubTotalPrice}*`}</p>
          </div>
        </Fragment>
      ) : (
        <div className='subscription-info'>
          <p>{t('navigation.subscriptionOverview.subscription')}</p>
          <p>{`${formattedSubscriptionPrice}*`}</p>
        </div>
      )}
      <p className='subscription-note'>
        {t('navigation.subscriptionOverview.note')}
      </p>
    </div>
  )
}

export default SubscriptionOverview
